import { CONTEXT_SECTION_TITLES } from '@lixionary-jira/domain';
import { CircleCheck, CircleDashed } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/forms';
import { ConfirmDialog } from '../../ui/Modal';
import { Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { SettingsNav } from './SettingsNav';

/** PRJ-01, PRJ-04–08: details, archive/restore, and setup completeness. */
export function ProjectSettingsPage() {
  const { key } = useParams();
  const [params] = useSearchParams();
  const admin = useIsAdmin();
  const q = useProject(key);
  usePageTitle(q.data ? `${q.data.name} · Project settings` : null);
  useCrumbs([{ label: q.data?.name ?? key ?? '', to: `/projects/${key}` }, { label: 'Settings' }]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirm, setConfirm] = useState<'archive' | 'restore' | null>(null);
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (q.data) {
      setName(q.data.name);
      setDescription(q.data.description);
    }
  }, [q.data]);
  const save = useAction(
    () => api.patch(`/projects/${key}`, { name, description, expectedVersion: q.data!.version }),
    { success: 'Saved', invalidate: [['project', key], ['projects']] },
  );
  const archive = useAction(
    (a: 'archive' | 'restore') =>
      api.post(`/projects/${key}/${a}`, { expectedVersion: q.data!.version, reason: reason || undefined }),
    {
      success: (r) => (confirm === 'archive' ? 'Project archived' : 'Project restored') + (r ? '' : ''),
      invalidate: [['project', key], ['projects']],
      onSuccess: () => setConfirm(null),
    },
  );
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <QueryView query={q}>
        {(p) => {
          const s = p.setup;
          const steps: [string, boolean, string, string][] = [
            ['Members', s.members > 0, `${s.members} member(s)`, 'members'],
            ...s.context.map((c): [string, boolean, string, string] => [
              CONTEXT_SECTION_TITLES[c.section],
              c.missing.length === 0,
              c.versionNumber
                ? c.missing.length
                  ? `v${c.versionNumber} · missing ${c.missing.length} heading(s)`
                  : `v${c.versionNumber}`
                : 'Not written',
              `../context/${c.section}`,
            ]),
            [
              'Repository',
              !!s.repository,
              s.repository ? s.repository.url : 'Not registered',
              'repository-builders',
            ],
            ['Builders', s.activeBuilders > 0, `${s.activeBuilders} active`, 'repository-builders'],
            [
              'Production environment',
              s.hasProductionEnvironment,
              s.completionTarget
                ? `Completion target: ${s.completionTarget}`
                : 'None — required before production release approval',
              'environments',
            ],
          ];
          return (
            <>
              <PageHeader
                title="Project settings"
                subtitle={
                  <>
                    Key <span className="mono ink">{p.key}</span> is immutable.
                  </>
                }
                actions={p.archived ? <Badge>Archived</Badge> : undefined}
              />
              <SettingsNav projectKey={p.key} />
              {params.get('setup') && (
                <Alert tone="success" title="Project created">
                  Finish the setup checklist below so people and AI can start working.
                </Alert>
              )}
              <div className="split">
                <div className="stack">
                  <Card title="Details">
                    <form
                      className="stack"
                      onSubmit={(e) => {
                        e.preventDefault();
                        save.mutate(undefined);
                      }}
                    >
                      <Field label="Name">
                        {(f) => (
                          <Input
                            {...f}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={p.archived}
                            required
                          />
                        )}
                      </Field>
                      <Field label="Description" optional>
                        {(f) => (
                          <Textarea
                            {...f}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            disabled={p.archived}
                          />
                        )}
                      </Field>
                      <ErrorAlert error={save.error} />
                      <div>
                        <Button type="submit" loading={save.isPending} disabled={p.archived}>
                          Save changes
                        </Button>
                      </div>
                    </form>
                  </Card>
                  <Card
                    title={p.archived ? 'Restore project' : 'Archive project'}
                    subtitle={
                      p.archived
                        ? 'Restoring allows new work. Previously revoked approvals and claims stay revoked.'
                        : 'Archived projects stay readable but accept no new tickets, comments, uploads, claims, or releases. Archiving is refused while claims or deployments are active.'
                    }
                  >
                    <div>
                      {p.archived ? (
                        <Button variant="secondary" onClick={() => setConfirm('restore')}>
                          Restore project
                        </Button>
                      ) : (
                        <Button variant="danger-outline" onClick={() => setConfirm('archive')}>
                          Archive project
                        </Button>
                      )}
                    </div>
                  </Card>
                </div>
                <Card title="Setup" tight>
                  <ul className="checklist">
                    {steps.map(([label, done, detail, to]) => (
                      <li key={label}>
                        {done ? (
                          <CircleCheck size={16} color="var(--color-success)" aria-label="Complete" />
                        ) : (
                          <CircleDashed size={16} color="var(--color-muted-soft)" aria-label="Incomplete" />
                        )}
                        <span className="stack" style={{ gap: 0 }}>
                          <Link
                            to={
                              to.startsWith('..')
                                ? `/projects/${p.key}/${to.slice(3)}`
                                : `/projects/${p.key}/settings/${to}`
                            }
                          >
                            {label}
                          </Link>
                          <span className="small muted" style={{ overflowWrap: 'anywhere' }}>
                            {detail}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <KeyValue
                    items={[
                      [
                        'Project ID',
                        <span key="i" className="mono small">
                          {p.id}
                        </span>,
                      ],
                    ]}
                  />
                </Card>
              </div>
              {confirm && (
                <ConfirmDialog
                  title={confirm === 'archive' ? `Archive ${p.name}?` : `Restore ${p.name}?`}
                  confirmLabel={confirm === 'archive' ? 'Yes, archive' : 'Restore project'}
                  variant={confirm === 'archive' ? 'danger' : 'primary'}
                  onClose={() => setConfirm(null)}
                  onConfirm={() => archive.mutate(confirm)}
                  pending={archive.isPending}
                  error={archive.error}
                >
                  <p>
                    {confirm === 'archive'
                      ? 'Outstanding approval requests are revoked. History and evidence remain readable.'
                      : 'The project becomes active again. Revoked approvals and claims are not revived.'}
                  </p>
                  <Field label="Reason" optional>
                    {(f) => <Input {...f} value={reason} onChange={(e) => setReason(e.target.value)} />}
                  </Field>
                </ConfirmDialog>
              )}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}
